import { z } from "zod";
export * from "./estate-categories.js";

export const uuidSchema = z.uuid();
export const slugSchema = z.string().min(1).max(180).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const isoDateTimeSchema = z.iso.datetime({ offset: true });
export const currencySchema = z.string().length(3).default("ZAR");
export const publicationStateSchema = z.enum(["draft", "awaiting_review", "published", "rejected", "archived"]);
export const listingStatusSchema = z.enum(["draft", "awaiting_review", "active", "on_show", "under_offer", "reserved", "sold", "withdrawn", "expired", "archived"]);
export const listingKindSchema = z.enum(["sale", "rental", "development", "vacant_land"]);
export const publicPrecisionSchema = z.enum(["exact", "approximate", "estate_centroid", "hidden"]);
export const planDisplayPrecisionSchema = z.enum(["exact", "approximate", "plan_only", "hidden"]);
export const mapReviewStateSchema = z.enum(["candidate", "draft", "awaiting_review", "approved", "rejected", "published", "unpublished"]);

export const positionSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export const pointGeometrySchema = z.object({ type: z.literal("Point"), coordinates: positionSchema });
export const polygonGeometrySchema = z.object({
  type: z.literal("Polygon"),
  coordinates: z.array(z.array(positionSchema).min(4)).min(1),
});
export const multiPolygonGeometrySchema = z.object({
  type: z.literal("MultiPolygon"),
  coordinates: z.array(z.array(z.array(positionSchema).min(4)).min(1)).min(1),
});
export const geometrySchema = z.union([pointGeometrySchema, polygonGeometrySchema, multiPolygonGeometrySchema]);

export const publicEstateSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string().min(1),
  shortName: z.string().nullable(),
  shortDescription: z.string().nullable(),
  disclaimer: z.string().min(1),
  area: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }),
  centre: pointGeometrySchema.nullable(),
  boundary: multiPolygonGeometrySchema.nullable(),
  bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
  camera: z.object({ zoom: z.number(), pitch: z.number(), bearing: z.number() }),
  accent: z.object({ primary: z.string(), secondary: z.string().nullable() }),
  attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  publishedAt: isoDateTimeSchema,
});

export const publicEstatePlanPointSchema = z.object({
  id: uuidSchema,
  kind: z.enum(["poi", "property", "development"]),
  label: z.string().min(1),
  category: z.string().nullable(),
  shortDescription: z.string().nullable(),
  description: z.string().nullable(),
  keyFacts: z.array(z.object({ label: z.string(), value: z.string() })),
  media: z.array(z.object({ id: uuidSchema, url: z.union([z.url(), z.string().regex(/^\/(?!\/)[^\s]*$/)]), alt: z.string(), caption: z.string().nullable(), sortOrder: z.number().int().nonnegative() })),
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  displayPrecision: planDisplayPrecisionSchema,
  listingReference: z.string().nullable(),
  listingSlug: slugSchema.nullable(),
  developmentSlug: slugSchema.nullable(),
});

export const publicEstatePlanSchema = z.object({
  id: uuidSchema,
  estateId: uuidSchema,
  estateSlug: slugSchema,
  version: z.number().int().positive(),
  imageUrl: z.union([z.url(), z.string().regex(/^\/(?!\/)[^\s]*$/)]),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  publishedAt: isoDateTimeSchema.nullable(),
  points: z.array(publicEstatePlanPointSchema),
});

export const publicRegionSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string(),
  description: z.string().nullable(),
  centre: pointGeometrySchema.nullable(),
});

export const publicAreaSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string(),
  description: z.string().nullable(),
  region: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }),
  centre: pointGeometrySchema.nullable(),
});

export const publicDevelopmentSchema = z.object({
  id: uuidSchema,
  slug: slugSchema,
  name: z.string(),
  estate: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }).nullable(),
  shortDescription: z.string().nullable(),
  description: z.string().nullable(),
  salesStatus: z.string().nullable(),
  centre: pointGeometrySchema.nullable(),
  bounds: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
  publishedAt: isoDateTimeSchema,
});

export const listingMediaSchema = z.object({
  id: uuidSchema,
  url: z.union([z.url(), z.string().regex(/^\/(?!\/)[^\s]*$/)]),
  alt: z.string(),
  caption: z.string().nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  sortOrder: z.number().int().nonnegative(),
});

export const publicAgentSchema = z.object({
  id: uuidSchema,
  displayName: z.string(),
  phone: z.string().nullable(),
  email: z.email().nullable(),
  photoUrl: z.url().nullable(),
});

export const publicListingSchema = z.object({
  id: uuidSchema,
  reference: z.string().min(1),
  slug: slugSchema,
  kind: listingKindSchema,
  status: z.enum(["active", "on_show", "under_offer", "reserved"]),
  title: z.string().min(1),
  summary: z.string().nullable(),
  description: z.string().nullable(),
  price: z.number().nonnegative().nullable(),
  priceQualifier: z.string().nullable(),
  priceVisible: z.boolean(),
  currency: currencySchema,
  bedrooms: z.number().nonnegative().nullable(),
  bathrooms: z.number().nonnegative().nullable(),
  garages: z.number().nonnegative().nullable(),
  parking: z.number().nonnegative().nullable(),
  erfSizeM2: z.number().nonnegative().nullable(),
  floorSizeM2: z.number().nonnegative().nullable(),
  propertyType: z.object({ slug: slugSchema, label: z.string() }).nullable().optional(),
  estate: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }),
  development: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }).nullable(),
  precinct: z.object({ id: uuidSchema, slug: slugSchema, name: z.string() }).nullable(),
  publicPrecision: publicPrecisionSchema,
  location: pointGeometrySchema.nullable(),
  features: z.array(z.object({ key: z.string(), label: z.string(), value: z.union([z.string(), z.number(), z.boolean()]) })),
  media: z.array(listingMediaSchema),
  agents: z.array(publicAgentSchema),
  featured: z.boolean(),
  publishedAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const listingFiltersSchema = z.object({
  // "buy" is a buyer-facing search mode that intentionally combines
  // ordinary sale listings with new-build homes attached to a collection.
  kind: z.union([listingKindSchema, z.literal("buy"), z.literal("all")]).optional(),
  estate: slugSchema.optional(),
  development: slugSchema.optional(),
  propertyType: slugSchema.optional(),
  minPrice: z.coerce.number().nonnegative().optional(),
  maxPrice: z.coerce.number().nonnegative().optional(),
  bedrooms: z.coerce.number().nonnegative().optional(),
  bathrooms: z.coerce.number().nonnegative().optional(),
  status: z.enum(["active", "on_show", "under_offer", "reserved"]).optional(),
  vacantLand: z.coerce.boolean().optional(),
  onShow: z.coerce.boolean().optional(),
  featured: z.coerce.boolean().optional(),
  feature: z.union([z.string(), z.array(z.string())]).optional(),
  bounds: z.string().regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?,-?\d+(\.\d+)?$/).optional(),
  sort: z.enum(["newest", "price_asc", "price_desc", "featured"]).default("featured"),
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(24),
});

export const attributionSchema = z.object({
  referrer: z.string().max(2048).nullable().optional(),
  utmSource: z.string().max(200).nullable().optional(),
  utmMedium: z.string().max(200).nullable().optional(),
  utmCampaign: z.string().max(200).nullable().optional(),
  utmTerm: z.string().max(200).nullable().optional(),
  utmContent: z.string().max(200).nullable().optional(),
  gclid: z.string().max(300).nullable().optional(),
});

export const enquiryInputSchema = z.object({
  type: z.enum(["property", "estate", "development", "seller_valuation", "general", "brochure", "agent"]),
  name: z.string().trim().min(2).max(160),
  email: z.email().max(254).optional(),
  phone: z.string().trim().min(7).max(40).optional(),
  message: z.string().trim().min(5).max(5000),
  listingId: uuidSchema.optional(),
  estateId: uuidSchema.optional(),
  developmentId: uuidSchema.optional(),
  agentId: uuidSchema.optional(),
  pageUrl: z.url().max(2048),
  formVariant: z.string().max(100),
  consent: z.object({ accepted: z.literal(true), languageVersion: z.string().min(1).max(50), marketingOptIn: z.boolean().default(false) }),
  attribution: attributionSchema.default({}),
  honeypot: z.string().max(0).optional(),
}).refine((input) => Boolean(input.email || input.phone), { message: "An email address or phone number is required", path: ["email"] });

export const enquiryReceiptSchema = z.object({
  enquiryId: uuidSchema,
  leadId: uuidSchema,
  duplicate: z.boolean(),
  routing: z.object({ level: z.enum(["listing", "development", "estate", "area", "default"]), teamId: uuidSchema.nullable(), agentId: uuidSchema.nullable() }),
  message: z.string(),
});

export const apiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), correlationId: z.string(), details: z.unknown().optional() }),
});

export const paginationSchema = z.object({ page: z.number().int().positive(), perPage: z.number().int().positive(), total: z.number().int().nonnegative(), totalPages: z.number().int().nonnegative() });
export const paginatedListingsSchema = z.object({ data: z.array(publicListingSchema), pagination: paginationSchema });
export const publicMapOverlaySchema = z.object({
  id: uuidSchema, key: z.string(), name: z.string(), layerType: z.enum(["raster","vector","geojson","pmtiles","computed"]),
  sourceConfig: z.record(z.string(), z.unknown()), styleConfig: z.record(z.string(), z.unknown()),
  minZoom: z.number().nullable(), maxZoom: z.number().nullable(), sortOrder: z.number().int(), estateId: uuidSchema.nullable(), developmentId: uuidSchema.nullable(),
});

export type PublicEstate = z.infer<typeof publicEstateSchema>;
export type PublicRegion = z.infer<typeof publicRegionSchema>;
export type PublicArea = z.infer<typeof publicAreaSchema>;
export type PublicDevelopment = z.infer<typeof publicDevelopmentSchema>;
export type PublicListing = z.infer<typeof publicListingSchema>;
export type ListingFilters = z.infer<typeof listingFiltersSchema>;
export type EnquiryInput = z.infer<typeof enquiryInputSchema>;
export type EnquiryReceipt = z.infer<typeof enquiryReceiptSchema>;
export type PublicMapOverlay = z.infer<typeof publicMapOverlaySchema>;
export type PublicEstatePlan = z.infer<typeof publicEstatePlanSchema>;
export type PublicEstatePlanPoint = z.infer<typeof publicEstatePlanPointSchema>;
